import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme, alpha } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, Tooltip } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import menuItem, { categoryStyles } from 'menu-items';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Importar el hook de notificaciones
import { useNotifications } from 'contexts/NotificationContext';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

  // Usar el contexto de notificaciones
  const { pendingReservations, complaints } = useNotifications();

  const Icon = item.icon;
  const isSelected = customization.isOpen.findIndex((id) => id === item.id) > -1;

  // Obtener el color de categoría
  const categoryColor = item.category ? categoryStyles && categoryStyles[item.category]?.color : theme.palette.primary.main;
  const categoryBgColor = item.category
    ? categoryStyles && categoryStyles[item.category]?.bgColor
    : alpha(theme.palette.primary.main, 0.08);
  const categoryHoverColor = item.category
    ? categoryStyles && categoryStyles[item.category]?.hoverColor
    : alpha(theme.palette.primary.main, 0.12);

  const itemIcon = item?.icon ? (
    <Icon
      stroke={1.5}
      size="1.3rem"
      style={{
        color: isSelected ? categoryColor : 'inherit'
      }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        color: isSelected ? categoryColor : 'inherit'
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: MENU_OPEN, id });
    if (matchesSM) dispatch({ type: SET_MENU, opened: false });
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: MENU_OPEN, id: item.id });
    }
    // eslint-disable-next-line
  }, [pathname]);

  // Determinar el texto del badge según el ID del elemento
  const getBadgeText = () => {
    if (item.id === 'reservation' && pendingReservations > 0) {
      return pendingReservations.toString();
    }
    if (item.id === 'complaint' && complaints > 0) {
      return complaints.toString();
    }
    return item.badge?.text || null;
  };

  const badgeText = getBadgeText();

  return (
    <Tooltip title={item.tooltip || ''} placement="right" arrow>
      <ListItemButton
        {...listItemProps}
        disabled={item.disabled}
        sx={{
          borderRadius: `${customization.borderRadius}px`,
          mb: 0.5,
          alignItems: 'flex-start',
          backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
          py: level > 1 ? 1 : 1.25,
          pl: `${level * 24}px`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: categoryHoverColor,
            transform: 'translateX(4px)'
          },
          '&.Mui-selected': {
            backgroundColor: categoryBgColor,
            '&:hover': {
              backgroundColor: categoryHoverColor
            }
          }
        }}
        selected={isSelected}
        onClick={() => itemHandler(item.id)}
      >
        <ListItemIcon
          sx={{
            my: 'auto',
            minWidth: !item?.icon ? 18 : 36,
            color: isSelected ? categoryColor : 'inherit'
          }}
        >
          {itemIcon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={isSelected ? 'h5' : 'body1'}
              color={isSelected ? categoryColor : 'inherit'}
              sx={{
                fontWeight: isSelected ? 600 : 'normal',
                transition: 'color 0.2s ease-in-out'
              }}
            >
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                {item.caption}
              </Typography>
            )
          }
        />
        {/* Mostrar badge dinámico o estático según corresponda */}
        {(badgeText || item.badge) && (
          <Chip
            label={badgeText || item.badge?.text}
            color={item.badge?.color || 'primary'}
            size="small"
            sx={{
              ml: 1.25,
              pl: 0.5,
              height: 20,
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          />
        )}
        {/* Mostrar el chip antiguo si está definido */}
        {item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
